import * as React from 'react'
import Markdown, { uriTransformer } from 'react-markdown'
import breaks from 'remark-breaks'
import { useQuery } from 'react-apollo'
import { FormattedMessage } from 'react-intl'
import isMobile from 'ismobilejs'
import { useHistory, useLocation } from 'react-router-dom'

import { Flex, Heading, Paragraph, settings as settingsStyles, Rule } from '@dooer/react-ui-kit'
import notEmpty from '@dooer/not-empty/es'

import { AcademyCategoryIcon, AcademyCustomerCategory } from '../../__generated__/globalTypes'

import { useLocalStorageState } from '../../lib/utils'
import Footer from '../../sections/footer'
import LinkRenderer from '../../components/markdown-renderers/link-renderer'
import ImageRenderer from '../../components/markdown-renderers/image-renderer'
import Spinner from '../../components/spinner'

import Hero from './components/hero'
import AcademyBreadcrumb from './components/breadcrumb'
import SearchResultView from './search-result-view'
import { AcademyList } from './academy-article-list'

import { getAcademyRootCategoriesQuery } from './get-academy-root-categories-query'
import {
  DooerGetAcademyRootCategories,
  DooerGetAcademyRootCategoriesVariables,
  DooerGetAcademyRootCategories_academy_treeCategories_edges_node_children_edges_node_children_edges_node_articles as AcademyArticles,
  DooerGetAcademyRootCategories_academy_treeCategories_edges_node_children_edges_node_children_edges_node_children as AcademyCategoryChildren,
} from './__generated__/DooerGetAcademyRootCategories'
import Seo from '../../components/seo'

export interface IBreadcrumbTuple {
  title: string
  path: string
}

export interface IAcademyCategory {
  id: string
  title: string
  description?: string | null
  slug?: string | null
  path?: string | null
  icon: AcademyCategoryIcon | null
  articles?: AcademyArticles | null
  articlesWithOrder?: IAcademyArticle[]
  children?: AcademyCategoryChildren | null
  customerCategories?: AcademyCustomerCategory[] | null
}

export interface IAcademyArticle extends IAcademyCategory {
  category?: IAcademyCategory | null
}

function flattenCategoryTree(categories: IAcademyCategory[], breadcrumbTuples: IBreadcrumbTuple[]): IAcademyCategory[] {
  return categories.reduce((flatCategories, category) => {
    const childrenCategories = ((category.children && category.children.edges) || []).map((edge) => edge.node)
    return [
      ...flatCategories,
      ...flattenCategoryTree(childrenCategories, [
        ...breadcrumbTuples,
        {
          title: category.title,
          path: category.slug
            ? [...breadcrumbTuples.map((breadcrumbTuple) => breadcrumbTuple.path), category.slug].join('/')
            : '',
        },
      ]),
      {
        ...{
          ...category,
          path: category.slug
            ? [...breadcrumbTuples.map((breadcrumbTuple) => breadcrumbTuple.path), category.slug].join('/')
            : '',
        },
        breadcrumbTuples,
      },
    ]
  }, [] as IAcademyCategory[])
}

export function useSearchParams() {
  return new URLSearchParams(useLocation().search)
}

function getPathArticle(article: IAcademyArticle, flatCategoryTree: IAcademyCategory[]) {
  const category =
    article.category &&
    article.category.id &&
    flatCategoryTree.find((c) => c.id === (article.category && article.category.id))
  return category ? `${category.path}/${article.slug}` : article.slug
}

function ListLayout(props: { children: React.ReactNode; centerContent: boolean }) {
  return (
    <Flex
      alignItems={props.centerContent ? 'center' : 'flex-start'}
      flexGrow={1}
      justifyContent="center"
      marginBottom={isMobile().any ? 'large' : 'xxLarge'}
      marginTop={isMobile().any ? 'large' : 'xxLarge'}
      row
      width="100%"
    >
      {props.centerContent ? (
        <Flex flexWrap="wrap" justifyContent="center" maxWidth={1220} row width="100%">
          {props.children}
        </Flex>
      ) : (
        <Flex justifyContent="center" marginLeft="medium" marginRight="medium" maxWidth={700} row width="100%">
          <div style={{ width: isMobile().any ? '90%' : '100%' }}>{props.children}</div>
        </Flex>
      )}
    </Flex>
  )
}

function createBreadcrumbTuple(categories: IAcademyCategory[], articles: IAcademyArticle[], pathname: string) {
  return pathname
    .split('/')
    .map((path, i) => {
      const currentPath = pathname
        .split('/')
        .slice(0, i + 1)
        .join('/')

      const article = articles.find((a) => a.path === currentPath)?.title

      // We only want to link back to "top category" or the article itself, no intermediate categories
      return i !== 0 && !article
        ? null
        : {
            path: currentPath,
            title:
              article ||
              categories.find((c) => c.path === currentPath)?.title ||
              pathname.split('/').slice(i, i + 1)?.[0],
          }
    })
    .filter(notEmpty)
}

interface IAcademyProps {
  match: {
    params: string[]
  }
  location: {
    search: string
    pathname: string
  }
}

const Academy = (props: IAcademyProps) => {
  const query = useSearchParams()
  const academySearch = query.get('academySearch')
  const history = useHistory()
  const [customerCategory, setCustomerCategory] = useLocalStorageState<AcademyCustomerCategory>(
    '@@frontend-website/academyCustomerCategory',
    AcademyCustomerCategory.free
  )
  const [search, setSearch] = React.useState(academySearch)
  const { data, loading } = useQuery<DooerGetAcademyRootCategories, DooerGetAcademyRootCategoriesVariables>(
    getAcademyRootCategoriesQuery,
    {
      variables: {
        filter: 'targets any in ["public"]',
      },
    }
  )

  const currentPath = props?.match?.params?.[0]

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [props.location.pathname, props.location.search, currentPath])

  const flatCategoryTree = flattenCategoryTree(
    ((data && data.academy && data.academy.treeCategories && data.academy.treeCategories.edges) || []).map(
      (edge) => edge.node
    ),
    []
  )

  const rootCategories = flatCategoryTree.filter((c) => !(c.path && c.path.includes('/')))

  const articles = ((data && data.academy && data.academy.articles && data.academy.articles.edges) || []).map(
    (edge) => ({
      ...edge.node,
      path: getPathArticle(edge.node, flatCategoryTree),
    })
  )

  const { article, category } = {
    article: articles.find((a) => a.path === currentPath),
    category: flatCategoryTree.find((c) => c.path === currentPath),
  }

  const articlesOnCategory = category
    ? articles.filter((a) =>
        ((category && category.articles && category.articles.edges) || []).map((edge) => edge.node.id).includes(a.id)
      )
    : []

  const categoriesOnCategory: IAcademyCategory[] = category
    ? flatCategoryTree
        .filter((c) =>
          ((category.children && category.children.edges) || []).map((edge) => edge.node.id).includes(c.id)
        )
        .map((c) => ({
          ...c,
          articlesWithOrder: articles.filter((a) => a.category?.id === c.id),
        }))
    : rootCategories

  const isRoot =
    (props.location.pathname === '/hjalp' || props.location.pathname === '/hjalp/') && !props.location.search

  const pathTuples = createBreadcrumbTuple(flatCategoryTree, articles, currentPath || '/hjalp')

  function handleSubmitSearch() {
    if (search == null || search === '') {
      return
    }

    history.push({
      pathname: '/hjalp',
      search: `academySearch=${search}`,
    })
  }

  const helpUrl = 'https://www.dooer.com/hjalp'

  return (
    <Flex flexDirection="column" minHeight={`calc(100% - 70px)`}>
      <Seo
        description="Här hittar du Dooers akademi och artiklar."
        image="https://www.dooer.com/images/og/og_index.jpg"
        title="Dooer Akademi"
        url={
          article
            ? category
              ? `${helpUrl}/${category.slug}/${article.slug}`
              : article.category
              ? `${helpUrl}/${article.category.slug}/${article.slug}`
              : `${helpUrl}/${article.slug}`
            : category
            ? `${helpUrl}/${category.slug}`
            : helpUrl
        }
      />

      {(academySearch || currentPath) && (
        <AcademyBreadcrumb
          academySearch={academySearch}
          onSearchChange={(value) => setSearch(value)}
          onSubmit={handleSubmitSearch}
          pathTuples={pathTuples}
          search={search}
        />
      )}

      {!category && !article && !academySearch && (
        <Hero
          customerCategory={customerCategory}
          onCustomerCategoryChange={(value) => setCustomerCategory(value)}
          onSearchChange={(value) => setSearch(value)}
          onSubmit={handleSubmitSearch}
          search={search}
        />
      )}
      <ListLayout centerContent={isRoot || (!academySearch && !category && !article)}>
        {academySearch && !category && !article ? (
          <SearchResultView
            academySearch={academySearch}
            articlesWithPath={articles}
            location={props.location}
            search={search}
          />
        ) : (
          <>
            {loading ? (
              <Spinner />
            ) : !isRoot && !category && !article ? (
              <Heading>
                <FormattedMessage defaultMessage="Sidan kunde inte hittas…" id="academy.not-found" />
              </Heading>
            ) : article ? (
              <>
                <Heading level="h1" variant="regular">
                  {article.title}
                </Heading>
                <Rule strong variant="regular" />
                <Flex marginBottom="xLarge" />
                <Markdown
                  className="academy-markdown"
                  plugins={[breaks]}
                  renderers={{
                    link: (innerProps) => <LinkRenderer {...innerProps} articles={articles} />,
                    image: ImageRenderer,
                  }}
                  source={(article.markdown && article.markdown.source) || ''}
                  transformLinkUri={(uri) => (uri && uri.startsWith('dooer:') ? uri : uriTransformer(uri))}
                />
              </>
            ) : (
              <>
                {category && (
                  <Flex marginBottom="large">
                    <div>
                      <Heading level="h2" style={{ fontSize: 32, marginBottom: settingsStyles.spacing.small }}>
                        {category.title}
                      </Heading>
                      <Paragraph flush style={{ fontSize: 14, fontFamily: 'aktiv-grotesk' }}>
                        {category.description}
                      </Paragraph>
                    </div>
                  </Flex>
                )}
                <AcademyList
                  academySearch={academySearch}
                  articles={articlesOnCategory}
                  categories={categoriesOnCategory}
                  customerCategory={customerCategory}
                  iconStyle="brand"
                  isRoot={isRoot}
                />
              </>
            )}
          </>
        )}
      </ListLayout>
      <Footer inverted />
    </Flex>
  )
}

export default Academy
